/* eslint-disable new-cap */
import type { FetchResult } from "@apollo/client";
import type { ColumnDef } from "@tanstack/react-table";
import { t } from "i18next";
import _ from "lodash";
import { useCallback } from "react";
import type React from "react";

import {
  ActionsFormatter,
  CloningStatusFormatter,
  StatusChangeFormatter,
} from "./formatters";
import { checkSSHFormat } from "./management-modal/repository/utils";
import { formInitialValues } from "./management-modal/utils";

import type {
  ICredentials,
  IDockerImageAttr,
  IEnvironmentUrlAttr,
  IFormValues,
  IGitRootAttr,
  IGitRootData,
  IGitRootEventsData,
  IGitRootManagementDetailsAttr,
} from "../types";
import {
  formatBooleanHealthCheck,
  getAddGitRootCredentials,
  getUpdateGitRootCredentials,
} from "../utils";
import { priorityFormatter } from "features/git-roots/formatters/priority";
import { RootCriticality } from "gql/graphql";
import type {
  GetGitRootsViewFragmentFragment,
  GetRootsEnvironmentUrlsFragment,
} from "gql/graphql";
import { formatTypeCredentials } from "utils/credentials";
import { formatBoolean } from "utils/format-helpers";
import { translate } from "utils/translations/translate";

type TModalMessages = React.Dispatch<
  React.SetStateAction<{ message: string; type: string }>
>;

function useGitSubmit(
  addGitRoot: (
    variables: Record<string, unknown>,
  ) => Promise<FetchResult<unknown>>,
  groupName: string,
  isManagingRoot: false | { mode: "ADD" | "EDIT" },
  setModalMessages: TModalMessages,
  updateGitRoot: (
    variables: Record<string, unknown>,
  ) => Promise<FetchResult<unknown>>,
): ({
  branch,
  credentials,
  gitignore,
  id,
  includesHealthCheck,
  nickname,
  priority,
  url,
  useEgress,
  useVpn,
  useZtna,
}: IFormValues) => Promise<void> {
  return useCallback(
    async ({
      branch,
      credentials,
      gitignore,
      id,
      includesHealthCheck,
      nickname,
      priority,
      url,
      useEgress,
      useVpn,
      useZtna,
    }: IFormValues): Promise<void> => {
      setModalMessages({ message: "", type: "success" });
      if (isManagingRoot !== false) {
        const newUrl = checkSSHFormat(url.trim())
          ? `ssh://${url.trim()}`
          : url.trim();
        if (isManagingRoot.mode === "ADD") {
          await addGitRoot({
            variables: {
              branch: branch.trim(),
              credentials: getAddGitRootCredentials(credentials),
              criticality: _.isEmpty(priority) ? RootCriticality.Low : priority,
              gitignore,
              groupName,
              includesHealthCheck:
                formatBooleanHealthCheck(includesHealthCheck) ?? false,
              nickname,
              url: newUrl,
              useEgress,
              useVpn,
              useZtna,
            },
          });
        } else {
          await updateGitRoot({
            variables: {
              branch,
              credentials: getUpdateGitRootCredentials(credentials),
              criticality: _.isEmpty(priority) ? RootCriticality.Low : priority,
              gitignore,
              groupName,
              id,
              includesHealthCheck:
                formatBooleanHealthCheck(includesHealthCheck) ?? false,
              nickname,
              url: newUrl,
              useEgress,
              useVpn,
              useZtna,
            },
          });
        }
      }
    },
    [addGitRoot, groupName, isManagingRoot, setModalMessages, updateGitRoot],
  );
}

const getCredentials = (
  currentRow: IGitRootManagementDetailsAttr,
): ICredentials => {
  return _.isNull(currentRow.credentials)
    ? {
        arn: "",
        auth: "",
        azureOrganization: "",
        id: "",
        isPat: false,
        isToken: false,
        key: "",
        name: "",
        password: "",
        token: "",
        type: "",
        typeCredential: "",
        user: "",
      }
    : {
        ...currentRow.credentials,
        typeCredential: formatTypeCredentials(currentRow.credentials),
      };
};

const getEnvsFromResult = (
  envUrlsResult: GetRootsEnvironmentUrlsFragment | null | undefined,
): IEnvironmentUrlAttr[] =>
  _.isNil(envUrlsResult)
    ? ([] as IEnvironmentUrlAttr[])
    : envUrlsResult.group.gitEnvironmentUrls;

const getGitRootsFromResult = (
  rootsResult: GetGitRootsViewFragmentFragment | null | undefined,
): IGitRootAttr[] =>
  _.isNil(rootsResult)
    ? ([] as IGitRootAttr[])
    : rootsResult.group.gitRoots.edges.map(
        (edge): IGitRootAttr => edge.node as IGitRootAttr,
      );

const getInitialValues = (
  currentRow: IGitRootManagementDetailsAttr | undefined,
): IFormValues => {
  if (_.isUndefined(currentRow)) {
    return formInitialValues;
  }

  return {
    branch: currentRow.branch,
    cloningStatus: currentRow.cloningStatus,
    credentials: getCredentials(currentRow),
    gitEnvironmentUrls: currentRow.gitEnvironmentUrls,
    gitignore: currentRow.gitignore,
    hasExclusions: _.isEmpty(currentRow.gitignore) ? "no" : "yes",
    healthCheckConfirm: undefined,
    id: currentRow.id,
    includesHealthCheck: currentRow.includesHealthCheck,
    nickname: currentRow.nickname,
    priority: currentRow.criticality,
    state: currentRow.state,
    url: currentRow.url,
    useEgress: currentRow.useEgress,
    useVpn: currentRow.useVpn,
    useZtna: currentRow.useZtna,
  };
};

const formatRootsData = (
  rootsAttr: IGitRootAttr[],
  environmentUrls: IEnvironmentUrlAttr[],
): IGitRootData[] => {
  const envUrlsByRoot = environmentUrls.reduce<
    Record<string, IEnvironmentUrlAttr[]>
  >((previous, currentValue): Record<string, IEnvironmentUrlAttr[]> => {
    return {
      ...previous,
      [currentValue.rootId]: [
        ..._.get(previous, currentValue.rootId, [] as IEnvironmentUrlAttr[]),
        currentValue,
      ],
    };
  }, {});

  return rootsAttr.map((root): IGitRootData => {
    const gitEnvironmentUrls = _.get(
      envUrlsByRoot,
      root.id,
      [] as IEnvironmentUrlAttr[],
    );

    return {
      ...root,
      gitEnvironmentUrls,
    };
  });
};

const isAwsRole = (credentials: ICredentials): boolean => {
  return credentials.typeCredential === "AWSROLE" && credentials.arn === "";
};

const isSshRole = (credentials: ICredentials): boolean => {
  return credentials.typeCredential === "SSH" && credentials.key === "";
};

const getRepositoryTourStepsText = (
  credentials: ICredentials,
): string | undefined => {
  if (credentials.id === "") {
    if (
      credentials.typeCredential === "USER" &&
      (credentials.user === "" || credentials.password === "")
    ) {
      return translate.t("tours.addGitRoot.rootCredentials.user");
    }
    if (
      credentials.typeCredential === "TOKEN" &&
      (credentials.token === "" || credentials.azureOrganization === "")
    ) {
      return translate.t("tours.addGitRoot.rootCredentials.token");
    }
    if (isSshRole(credentials)) {
      return translate.t("tours.addGitRoot.rootCredentials.key");
    }
    if (isAwsRole(credentials)) {
      return translate.t("tours.addGitRoot.rootCredentials.arn");
    }
    if (credentials.typeCredential === "") {
      return translate.t("tours.addGitRoot.rootCredentials.type");
    }
  }

  return undefined;
};

const shouldHideAwsRole = (credentials: ICredentials): boolean => {
  return (
    credentials.typeCredential === "AWSROLE" && credentials.arn.length === 0
  );
};

const shouldHideToken = (credentials: ICredentials): boolean => {
  return (
    credentials.typeCredential === "TOKEN" &&
    (credentials.token.length === 0 ||
      credentials.azureOrganization.length === 0)
  );
};

const shouldHideRepositoryTourFooter = (credentials: ICredentials): boolean => {
  if (credentials.id === "") {
    if (credentials.typeCredential === "" || credentials.name.length === 0) {
      return true;
    }
    if (credentials.typeCredential === "SSH" && credentials.key.length === 0) {
      return true;
    }
    if (shouldHideAwsRole(credentials)) {
      return true;
    }
    if (shouldHideToken(credentials)) {
      return true;
    }
    if (
      credentials.typeCredential === "USER" &&
      (credentials.user.length === 0 || credentials.password.length === 0)
    ) {
      return true;
    }
  }

  return false;
};

const fromDockerImagesToEnvironmentUrl = (
  dockerImages: IDockerImageAttr[],
): IEnvironmentUrlAttr[] => {
  return dockerImages.map(
    (dockerImage): IEnvironmentUrlAttr => ({
      __typename: "GitEnvironmentUrl" as const,
      cloudName: null,
      countSecrets: 0,
      createdAt: dockerImage.createdAt,
      createdBy: dockerImage.createdBy,
      id: dockerImage.rootId,
      include: dockerImage.include,
      isProduction: null,
      requiresAuthentication: null,
      root: {
        id: dockerImage.root.id,
        state: dockerImage.root.state,
        url: dockerImage.root.url,
      },
      rootId: dockerImage.rootId,
      url: dockerImage.uri,
      urlType: "Docker Image",
      useEgress: false,
      useVpn: false,
      useZtna: false,
    }),
  );
};

const getColumns = (
  canUpdateRootState: boolean,
  canSyncGitRoot: boolean,
  onUpdate: (row: Record<string, string>) => void,
  onSync: (row: IGitRootData) => void,
  loading: boolean,
): ColumnDef<IGitRootEventsData>[] => [
  {
    accessorKey: "url",
    header: String(t("group.scope.git.repo.url.header")),
    minSize: 400,
    size: 400,
  },
  {
    accessorKey: "branch",
    header: String(t("group.scope.git.repo.branch.header")),
    minSize: 100,
    size: 100,
  },
  {
    accessorKey: "state",
    cell: (cell): JSX.Element =>
      StatusChangeFormatter(cell, canUpdateRootState, onUpdate),
    header: String(t("group.scope.common.state")),
    id: "status",
    minSize: 120,
    size: 120,
  },
  {
    accessorKey: "priority",
    cell: (cell): JSX.Element => {
      const value = cell.row.original.criticality;

      if (value === null) {
        return priorityFormatter("NONE");
      }

      return priorityFormatter(value);
    },
    header: String(t("group.scope.git.repo.priority.header")),
    minSize: 90,
    size: 90,
  },
  {
    accessorKey: "cloningStatus",
    cell: CloningStatusFormatter,
    header: String(t("group.scope.git.repo.cloning.status")),
    id: "cloning_status",
    minSize: 150,
    size: 150,
  },
  {
    accessorFn: (row: IGitRootEventsData): string | undefined =>
      formatBoolean(row.includesHealthCheck as boolean),
    header: String(t("group.scope.git.healthCheck.tableHeader")),
    id: "hck",
  },
  {
    accessorKey: "nickname",
    header: String(t("group.scope.git.repo.nickname.text")),
  },
  ...(canSyncGitRoot
    ? [
        {
          cell: (cell): JSX.Element => ActionsFormatter(cell, onSync, loading),
          header: String(t("group.scope.git.repo.cloning.sync")),
          id: "sync",
          minSize: 100,
        } as ColumnDef<IGitRootEventsData>,
      ]
    : []),
];

export {
  formatRootsData,
  getCredentials,
  getColumns,
  getEnvsFromResult,
  getGitRootsFromResult,
  getInitialValues,
  getRepositoryTourStepsText,
  shouldHideRepositoryTourFooter,
  useGitSubmit,
  fromDockerImagesToEnvironmentUrl,
};
